import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import parse from 'html-react-parser';

import {
  LeagueDetails,
  LocationLeagueDetails,
  SessionLeagueDetails,
} from '@/modules/league/new-components/types';

interface InfoProps {
  details: LeagueDetails;
  session: SessionLeagueDetails;
  location: LocationLeagueDetails;
}

const Info = ({ details, session, location }: InfoProps) => {
  const renderDescription = (title: string, content: string) => (
    <>
      <div className='mt-2 text-lg font-medium'>{title}</div>
      <div className='mb-1 text-base text-gray-600'>
        {content.split(/\n/).map((line: string, index: number) => (
          <p key={index}>{parse(line)}</p>
        ))}
      </div>
    </>
  );

  const renderSessionDetails = () => (
    <div className='description mb-6'>
      <div className='mb-4 flex w-full flex-col gap-3 rounded-lg bg-gray-100 p-4 text-gray-600'>
        <div>
          <span className='font-bold'>Skill: </span>
          {session?.RankRangeForUI}
        </div>
        <div>
          <span className='font-bold'> Age: </span>
          {session?.AgeRangeForUI}
        </div>
        <div>
          <span className='font-bold'> Max Players Per Session: </span>
          {session?.MaxAttendies < 0 ? 'Unlimited' : session.MaxAttendies}
        </div>

        <div>
          <span className='font-bold'> Allow Non-Club Members: </span>
          {session?.AllowNonMemberSignUp === true ? 'Yes' : 'No'}
        </div>
        {session.RankingType === 'Factors' || session.RankingType === 'Custom' ? (
          <div>
            <span className='font-bold'> Max Movement Rule: </span>
            {parseFloat(session?.MaxMovementRule) < 0 ? 'Unlimited' : session.MaxMovementRule}
          </div>
        ) : null}

        <div>
          <span className='font-bold'> 1st Choice Group Size: </span>
          {session?.GroupPlayerCountBreakdown_DefaultChoice1}
        </div>
        <div>
          <span className='font-bold'> 2nd Choice Group Size: </span>
          {session?.GroupPlayerCountBreakdown_DefaultChoice2}
        </div>
      </div>
      <div className='mt-2'>
        <div className='text-lg font-medium'>Signup / Payment Type</div>

        <div className=' my-1 flex-row text-base text-gray-600'>
          <div>
            {' '}
            {session.PlayerSignupAndPaymentType === 'EachGameDay'
              ? '(Open League)'
              : '(Closed League)'}
          </div>
          <div>
            {session.PlayerSignupAndPaymentType === 'EachGameDay'
              ? 'Each day the league is played the participants sign-up (and pay if applicable). Signup could be through the application or onsite, whichever the director specified. It is "open" to players entering anytime during the session.'
              : 'Prior to the session starting, participants sign-up (and pay if applicable) for the entire session. It is "closed" to entry after the session starts.'}
          </div>
        </div>
        <div className='mt-2 text-lg font-medium'>New Players Start</div>

        <div className=' my-1 flex-row text-base text-gray-600'>
          <div>{session.WhereAreNewPlayersStarted === 'Bottom' ? 'At Bottom' : 'At Rating'}</div>
          <div>
            {session.WhereAreNewPlayersStarted === 'Bottom'
              ? 'Each new player/team starts at the bottom of the league and works their way up'
              : 'Each new player/team starts at the location of their rating. The rating used will be Club, Tournament, or Self.'}
          </div>
        </div>
        <div className='mt-2 text-lg font-medium'>Ranking Type</div>

        <div className=' my-1 flex-row text-base  text-gray-600'>
          <div className='font-semibold'>
            {session?.RankingType === 'ClubRating'
              ? 'Club Rating'
              : session?.RankingType === 'Factors'
                ? 'Percentage of Points (POP)'
                : session.RankingType === 'UpDown'
                  ? 'Up Down (King of the Court)'
                  : session.RankingType === 'Custom'
                    ? 'Custom'
                    : session.RankingType === 'DUPRRating'
                      ? 'DUPR'
                      : null}
          </div>
          <div className='flex flex-col '>
            <div>
              {session?.RankingType === 'ClubRating'
                ? 'Club rating system will determine the ranking of player after games are played each game day.'
                : session?.RankingType === 'Factors'
                  ? '% Earned to Possible, Match Wins. (If there is a tie after these 2 factors are calculated then the higher ranked player the previous week takes precedence.)'
                  : session.RankingType === 'UpDown'
                    ? 'After the all games are completed, ' +
                      session.RankingType_UpDown_TotalMoving +
                      ' Players move up to a higher or down to a lower group.'
                    : session.RankingType === 'Custom'
                      ? 'Set custom point values for winning, losing, and playing'
                      : session.RankingType === 'DUPRRating'
                        ? 'DUPR'
                        : null}
            </div>
            {session.RankingType === 'Custom' ? (
              <div className=' mt-4 px-4'>
                <div>
                  Points for playing a day:
                  {' ' + session?.RankingType_Custom_PointsPerPlayingGame}
                </div>
                <div>
                  Points for winning a game:
                  {' ' + session?.RankingType_Custom_PointsPerWinningGame}
                </div>
                <div>
                  Points lost for losing a game:
                  {' ' + session?.RankingType_Custom_PointsLostPerLosingGame}
                </div>
                <div>
                  Points for winning a match:
                  {' ' + session?.RankingType_Custom_PointsPerWinningMatch}
                </div>
                <div>
                  Points lost for losing a match:
                  {' ' + session?.RankingType_Custom_PointsLostPerLosingMatch}
                </div>
                <div>
                  Points for winning a straight match:
                  {' ' + session?.RankingType_Custom_PointsPerWinningStraightMatch}
                </div>
                <div>
                  Points lost for losing a straight match:
                  {' ' + session?.RankingType_Custom_PointsLostPerLosingStraightMatch}
                </div>
              </div>
            ) : null}
            {session.RankingType === 'UpDown' ? (
              <div className='my-4 px-4'>
                <div>
                  Number of Up Down Rounds ={' '}
                  {session?.RankingType_UpDown_NumberOfSequences
                    ? session?.RankingType_UpDown_NumberOfSequences
                    : 0}{' '}
                </div>
                <div>
                  {' '}
                  How many Up Down rounds per game day. After round 1 all players will move up or
                  down and then play will continue again in their new groups.
                </div>
              </div>
            ) : null}
            {session.RankingType === 'UpDown' ? (
              <>
                <div className=' mt-4 px-4'>
                  <div>
                    Total Players Moving ={' '}
                    {session?.RankingType_UpDown_TotalMoving
                      ? session?.RankingType_UpDown_TotalMoving
                      : 0}{' '}
                  </div>
                  <div>
                    This is the total number of players to move up & the total number of players to
                    move down.
                    <br />
                  </div>
                </div>
                {session.WeekMiss_NumberOfWeeks >= 0 ||
                session.RankingType_UpDown_WeekMiss_SubtractAmount > 0 ? (
                  <>
                    <div className='mb-4 mt-8 px-4'>
                      <div>
                        {' '}
                        Days Grace ={' '}
                        {session.WeekMiss_NumberOfWeeks ? session.WeekMiss_NumberOfWeeks : 0}{' '}
                      </div>
                      <div>The total amount of missed days before a penalty is applied.</div>
                    </div>
                    <div className='my-4 px-4'>
                      <div>
                        Miss Game Day Rank Penalty ={' '}
                        {session?.RankingType_UpDown_WeekMiss_SubtractAmount
                          ? session?.RankingType_UpDown_WeekMiss_SubtractAmount
                          : 0}
                      </div>
                      <div>The total number of rank positions a player will move as a penalty.</div>
                    </div>
                    <div className=' my-4 px-4'>
                      <div>
                        Number of Miss Game Days in Row to Penalize =
                        {session?.RankingType_UpDown_NumberOfMissWeeksInRowToPenalize
                          ? session?.RankingType_UpDown_NumberOfMissWeeksInRowToPenalize
                          : 0}
                      </div>
                      <div>This is the number of weeks in a row a player can be penalized.</div>
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {session.UpDown_NumberOfTeams_AdditionToNonUpDownRankingType > 0 ? (
          <div className=' mb-1 mt-4 flex-row  text-base text-gray-600'>
            <div className='font-semibold'>Up Down Count (For 2nd round)</div>
            <div className='flex flex-col '>
              <div>
                <p> Games days are split into 2 rounds. </p>
                <p>
                  The 1st round Players play everyone in their group once and then the top and
                  bottom X number of players move up and down.
                  <br />
                  The 2nd round Players are matched up again and play another set of games with the
                  new players moved into their group from above and below.
                  <br />
                  Example: The buttom 2 from group three move to group four and the top 2 from group
                  three move to group two.
                  <br />
                  AFTER the second round is completed ranking will be calculated based on the
                  Ranking Type selected.
                </p>
              </div>
            </div>
          </div>
        ) : null}
        {session.NumberOfRoundRobins > 1 ? (
          <div className=' my-1 flex-row text-base  text-gray-600'>
            <div className='font-semibold'>Number Of Round Robins</div>
            <div className='flex flex-col '>
              <div>The total number of rounds played within a group for that game day.</div>
            </div>
          </div>
        ) : null}
        {parseInt(session.Group1_MatchDateBonus) > 0 ? (
          <div className=' mb-1 mt-4 flex-row  text-base text-gray-600'>
            <div className='font-semibold'>Game Day Difficulty Bonus Points</div>
            <div className='flex flex-col '>
              <div>
                <p>Additional points to groups based on their difficutly.</p>
                <p>
                  Those groups will have those points added to their overall total each game day
                  they play in that group.
                </p>
                <div className=' mt-4 px-4'>
                  <div>
                    Group 1:
                    {' ' + session?.Group1_MatchDateBonus}
                  </div>
                  <div>
                    Group 2:
                    {' ' + session?.Group2_MatchDateBonus}
                  </div>
                  <div>
                    Group 3:
                    {' ' + session?.Group3_MatchDateBonus}
                  </div>
                  <div>
                    Group 4:
                    {' ' + session?.Group4_MatchDateBonus}
                  </div>
                  <div>
                    Group 5:
                    {' ' + session?.Group5_MatchDateBonus}
                  </div>
                  <div>
                    Group 6:
                    {' ' + session?.Group6_MatchDateBonus}
                  </div>
                  <div>
                    Group 7:
                    {' ' + session?.Group7_MatchDateBonus}
                  </div>
                  <div>
                    Group 8:
                    {' ' + session?.Group8_MatchDateBonus}
                  </div>
                  <div>
                    Group 9:
                    {' ' + session?.Group9_MatchDateBonus}
                  </div>
                  <div>
                    Group 10:
                    {' ' + session?.Group10_MatchDateBonus}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderLocationInformation = () => (
    <div className='mb-4 flex w-full flex-col gap-3 rounded-lg bg-gray-100 p-4 text-gray-600'>
      <div>
        <FontAwesomeIcon className='mr-2' icon={faLocationDot} />
        <span className='font-bold'>{session?.Location_Title}</span>
        {` ${location.StreetAddress}, ${location?.Location_City} ${location?.StateTitle}, ${location?.Zip}, ${location.CountryTitle}`}
      </div>
      <div>
        <span className='font-bold'>Surface Type: </span>
        {location?.SurfaceTypeTitle}
      </div>
      <div>
        <span className='font-bold'> Play Area Type: </span>
        {location?.PlayAreaTypeTitle}
      </div>
      <div>
        <span className='font-bold'> Net Type: </span>
        {location?.NetTypeTitle}
      </div>
      {location.BallUsed !== '' ? (
        <div>
          <span className='font-bold'> Ball Used: </span>
          {location.BallURL !== null ? (
            <CustomLink
              href={`${location.BallURL}`}
              className='hover:underline hover:decoration-white hover:underline-offset-2'
            >
              {location?.BallUsed}
            </CustomLink>
          ) : (
            location?.BallUsed
          )}
        </div>
      ) : null}
      {location.NumberOfCourts > 0 ? (
        <div>
          <span className='font-bold'> Number of Courts: </span>
          {location?.NumberOfCourts}
        </div>
      ) : null}

      <div>
        <span className='font-bold'> Parking Fee: </span>
        {location?.IsParkingFee === true ? 'Paid Parking' : 'No Fee'}
      </div>
    </div>
  );

  const renderParking = () => (
    <>
      <hr className='my-8' />
      <div className='my-10'>
        <div className='mb-6 text-2xl font-bold text-gray-900'>Parking</div>
        <div className='mb-4 flex w-full flex-col gap-3 rounded-lg bg-gray-100 p-4 text-gray-600'>
          <div>{location.ParkingInformation}</div>
        </div>
      </div>
    </>
  );

  return (
    <div className='md:mx-4 lg:mt-0 lg:w-2/3'>
      <div>
        <div className='mb-6 text-2xl font-bold text-gray-900'>League Details</div>
        <div className='description '>
          <div className='mt-2'>
            <div className='text-lg font-medium'>
              {details?.PlayerGroupTitle} {details?.FormatTitle}
            </div>
            <div className='mb-1 text-base text-gray-600'>{details?.PlayerGroupDescription}</div>
            <div className='mt-2 text-lg font-medium'>Type: {details?.LeagueTypeTitle}</div>

            <div className='mb-1 text-base text-gray-600'>{details?.LeagueTypeDescription}</div>

            <div className='mt-2 text-lg font-medium'>Player Type: {details?.PlayerTypeTitle}</div>

            <div className='mb-1 text-base text-gray-600'>{details?.PlayerTypeDescription}</div>
            {details?.Description.length
              ? renderDescription('Description', details?.Description)
              : null}
            {details?.AdditionalInformation.length
              ? renderDescription('Additional Information', details?.AdditionalInformation)
              : null}
            {details?.RefundPolicy.length
              ? renderDescription('Refund Policy', details?.RefundPolicy)
              : null}
          </div>
        </div>
      </div>
      <hr className='my-8' />
      <div className='my-10'>
        <div className='mb-6 text-2xl font-bold text-gray-900'>Session Details</div>
        {renderSessionDetails()}
      </div>
      <hr className='my-8' />
      <div className='my-10'>
        <div className='mb-6 text-2xl font-bold text-gray-900'>Location Information</div>
        {renderLocationInformation()}
      </div>
      {location?.ParkingInformation ? renderParking() : null}
    </div>
  );
};

export default Info;
